import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  IndicatorDTOHackDataSourceQueryReasonEnum,
  SloDTOFromJSON,
  SloQualityResultItemDTOQualityCheckerIdEnum,
} from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { BooleanParam, useQueryParam } from 'use-query-params';
import Button from '@atlaskit/button/new';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';

import { apiFetch } from '@/services';
import SloPageData from '@/components/composed/new-slo-views/expanded/data';
import SloPageHeader from '@/components/composed/new-slo-views/expanded/header';
import SloPageSummary from '@/components/composed/new-slo-views/expanded/summary';
import LowTrafficHelper from '@/components/composed/new-slo-views/expanded/low-traffic-helper';
import {
  CapabilityEntityLink,
  KeyValue,
  Item,
  SkeletonBlock,
  useActions,
  ExternalLinkAction,
} from '@/components/primitive';
import SfxIcon from '@/components/primitive/sfx-icon';
import {
  breakWord,
  flexCenter,
  flexStart,
  spaceGapCompact,
  spaceGapMinimum,
  spaceTopComfortable,
  spaceTopCozy,
  spaceTopMinimum,
} from '@/styles';
import { joinEventsToSlo } from '@/ui';
import { SloEventsFromSingleSLO } from '@/api';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import RevisionLogs from '@/components/primitive/revision-logs';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import {
  useFetchErrorController,
  useLocationController,
  useNavigationController,
} from '@/controllers';
import { FetchError } from '@/services/api-service/api-service';
import { applySloDefaults, isHighCardinality } from '@/views/slo-view/utils';
import { querySpec } from '@/services/api-service/api-queries';
import { stringReplaceLink } from '@/utils';
import { SLOIndicatorWithGroupType } from '@/components/composed/slo-group/types';

import * as styles from './styles';
import LowPriorityWarning from './low-priority-warning';
import HighCardinalityWarning from './high-cardinality-warning';

const SLOView: React.FC = () => {
  const { publicId: publicIdParam } = useParams<{ publicId: string }>();
  const publicId = publicIdParam ?? '';

  const { onError } = useFetchErrorController();
  const { onNavigation } = useNavigationController();
  const { isSLOGroupMemberPage } = useLocationController();

  const { data: rawSloData, error, isInitialLoading: sloIsLoading } = useQuery(
    ['slo', publicId],
    () => apiFetch(`${isSLOGroupMemberPage ? '/slogroup' : ''}/slo/${publicId}`),
    { onError }
  );
  const { data: rawEventData } = useQuery(
    ['events', publicId],
    () => apiFetch(`/slo/${publicId}/events`),
    { onError }
  );

  const sloGroupPublicId = rawSloData?.indicator?.sloGroupPublicId || publicId;
  const indicator: SLOIndicatorWithGroupType = rawSloData?.indicator;

  // HOT API only supports group publicId, not member key.
  const { queryKey: sloIncidentsKey, path: sloIncidentsPath } = querySpec('hot.slo.{id}', {
    id: isSLOGroupMemberPage ? sloGroupPublicId : publicId,
  });
  const { data: sloIncidentsData } = useQuery(sloIncidentsKey, () => apiFetch(sloIncidentsPath), {
    onError,
    // only fetch if SLO data is loaded so we know if it's a group member or not
    enabled: !sloIsLoading,
  });

  const { queryKey: lowTrafficStatusKey, path: lowTrafficStatusPath } = querySpec(
    'slo.{id}.lowTrafficStatus',
    {
      id: publicId,
    }
  );
  const { data: lowTrafficStatusData } = useQuery(
    lowTrafficStatusKey,
    () => apiFetch(lowTrafficStatusPath),
    {
      onError,
    }
  );

  const data = useMemo(
    () =>
      rawSloData &&
      joinEventsToSlo(
        SloEventsFromSingleSLO(rawEventData ?? {}),
        sloIncidentsData || [],
        applySloDefaults(SloDTOFromJSON(rawSloData))
      ),
    [rawSloData, rawEventData, sloIncidentsData]
  );

  const [runbookParam, setRunbookParam] = useQueryParam('runbook', BooleanParam);
  const { onEdit } = useActions(publicId, data?.name, 'slo');

  const [isLoadingRunbook, setIsLoadingRunbook] = React.useState(!!runbookParam);
  const [showRunbookWarning, setShowRunbookWarning] = React.useState(false);
  const [showLowResolutionWarning, setShowLowResolutionWarning] = React.useState(false);

  React.useEffect(() => {
    if (runbookParam && data?.runbookUrl) {
      window.location.href = data.runbookUrl;
    } else if (data && !data?.runbookUrl) {
      setIsLoadingRunbook(false);
      if (runbookParam) {
        setShowRunbookWarning(true);
        setRunbookParam(undefined);
      }
    } else if (!runbookParam && data?.runbookUrl) {
      setShowRunbookWarning(false);
    }
  }, [data, runbookParam, setRunbookParam]);

  useEffect(() => {
    if (error instanceof FetchError && error.response.status === 404) {
      onNavigation('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!data?.indicator?.dataSourceQueryReason || !data?.quality?.sloQualityResultItems) {
      setShowLowResolutionWarning(false);
      return;
    }

    const isResolutionDependent = data.quality.sloQualityResultItems.find(
      (item: { qualityCheckerId: string }) =>
        item.qualityCheckerId ===
        SloQualityResultItemDTOQualityCheckerIdEnum.ResolutionDependentOperators
    )?.hasQualityIssue;

    // Should only show warning if the latest collection reason is low resolution and the query is resolution dependent
    if (
      data.indicator.dataSourceQueryReason !==
        IndicatorDTOHackDataSourceQueryReasonEnum.CollectionLowResolution ||
      !isResolutionDependent
    ) {
      setShowLowResolutionWarning(false);
      return;
    }

    setShowLowResolutionWarning(true);
  }, [data]);

  if (!rawSloData || sloIsLoading || isLoadingRunbook) return <SkeletonBlock />;

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title={data.name} scope="SLO">
        <div css={styles.root}>
          <LowPriorityWarning isLowPriority={data.isLowPriority} />
          <div css={styles.parentBlock}>
            <KeyValue title="Parent Capability">
              <CapabilityEntityLink
                name={data.parent.name}
                publicId={data.parent.publicId}
                shouldTruncate
                truncateLength="750px"
              />
            </KeyValue>
          </div>
          {isSLOGroupMemberPage && (
            <div css={[flexStart, spaceTopComfortable]}>
              <Button
                appearance="primary"
                onClick={() => {
                  onNavigation(`/slo-group/${sloGroupPublicId}`);
                }}
              >
                <span css={[flexStart, spaceGapMinimum]}>
                  <ChildIssuesIcon size="small" label="" />
                  <span>View SLO group page</span>
                </span>
              </Button>
            </div>
          )}
          <div css={isSLOGroupMemberPage ? spaceTopCozy : spaceTopComfortable}>
            <SloPageHeader
              publicId={data.publicId}
              name={data.name}
              type={data.type}
              performance={data.performance}
              objective={data.objective}
              indicator={indicator}
              isDraft={data.isDraft}
              isAlertable={data.indicator?.isAlertable}
              isSkeleton={data.isSkeleton}
              parentCapabilityPublicId={data.parent.publicId}
              lowTrafficStatus={lowTrafficStatusData}
            />
          </div>
          <div css={[spaceTopMinimum, breakWord]}>
            <h3>{data.name}</h3>
          </div>
          {showRunbookWarning && (
            <div css={[spaceTopComfortable]}>
              <SectionMessage
                appearance="warning"
                actions={[
                  <SectionMessageAction key="add-runbook-url" onClick={onEdit}>
                    Add Runbook URL
                  </SectionMessageAction>,
                ]}
              >
                The Runbook URL for this SLO has not been configured.
              </SectionMessage>
            </div>
          )}
          {data.description && (
            <div css={[spaceTopComfortable, styles.descriptionBlock]}>
              <Item title="Description">
                <p>{stringReplaceLink(data.description)}</p>
              </Item>
            </div>
          )}
          <div css={spaceTopComfortable}>
            <SloPageSummary
              performance={data.performance}
              objective={data.objective}
              isSkeleton={data.isSkeleton}
              quality={data.quality}
            />
          </div>
          <div css={spaceTopComfortable}>
            <SectionMessage appearance="information">
              <div css={[flexCenter, spaceGapCompact]}>
                <span>
                  Tome&apos;s data collection is delayed by up to 24 hrs, so this graph doesn&apos;t
                  show real-time data. Use{' '}
                </span>
                <div css={styles.inlineSFxIconStyles}>
                  <SfxIcon />
                </div>
                <span>for real-time monitoring.</span>
              </div>
            </SectionMessage>
          </div>
          <LowTrafficHelper
            publicId={data.publicId}
            objective={data.objective}
            lowTrafficStatus={lowTrafficStatusData}
          />
          {showLowResolutionWarning && (
            <div css={spaceTopComfortable}>
              <SectionMessage appearance="warning">
                Data for this SLO has been retrieved at a daily resolution. As your SLO uses{' '}
                <ExternalLinkAction
                  text="resolution-dependent operators"
                  href="https://developer.atlassian.com/platform/slos/tome/slo-quality/#resolution-dependent-operators"
                />
                , you might see data discrepancies with SignalFx until minutely datapoints have been
                retrieved.
              </SectionMessage>
            </div>
          )}
          {isHighCardinality(data.quality) && <HighCardinalityWarning />}
          <div css={styles.sloPageDataSectionStyles}>
            <SloPageData
              name={data.name}
              publicId={data.publicId}
              indicator={data.indicator}
              performance={data.performance}
              objective={data.objective}
              quality={data.quality}
              tags={data.tag}
              type={data.type}
              serviceId={data.serviceId}
              suppressLowTrafficAlerts={data.suppressLowTrafficAlerts}
              isUltraSteepBurnEnabled={data.isUltraSteepBurnEnabled}
              lowTrafficThreshold={data.lowTrafficThreshold}
            />
          </div>
          {!isSLOGroupMemberPage && (
            <div css={spaceTopComfortable}>
              {publicId && <RevisionLogs withTitle lazyload entityType="slo" publicId={publicId} />}
            </div>
          )}
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

export default SLOView;
